import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid, Chip, Divider } from '@mui/material';
import { formatDateBr as formatDateBrGlobal } from '../../utils/formatters/dateUtils';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportStyledField from './ReportStyledField.jsx';
import ReportWidget from './ReportWidget';

const formatDateBr = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const formatted = formatDateBrGlobal(value);
  return formatted || String(value);
};

const humanizeKey = (key) => {
  if (!key) return '';
  const normalized = String(key).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatGenericValue = (key, value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }
  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }
  if (typeof value === 'string') {
    if (key && String(key).toLowerCase().includes('data')) {
      return formatDateBr(value);
    }
    return value;
  }
  if (Array.isArray(value) || typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const normalizeSigefData = (sigefRaw) => {
  const parcels = [];

  if (!sigefRaw) {
    return {
      parcels,
      globalInfo: {
        codigoImovel: null,
        municipio: null,
        uf: null,
        situacao: null,
      },
    };
  }

  try {
    if (Array.isArray(sigefRaw)) {
      sigefRaw.forEach((item) => {
        if (!item) return;
        if (Array.isArray(item.parcels)) {
          parcels.push(...item.parcels);
        } else {
          parcels.push(item);
        }
      });
    } else if (typeof sigefRaw === 'object') {
      if (Array.isArray(sigefRaw.parcels)) {
        parcels.push(...sigefRaw.parcels);
      } else if (Array.isArray(sigefRaw.details)) {
        parcels.push(...sigefRaw.details);
      } else {
        parcels.push(sigefRaw);
      }
    }
  } catch {
    // Mantém lista vazia em caso de erro
  }

  const rootFirst = Array.isArray(sigefRaw)
    ? sigefRaw.find((item) => item && typeof item === 'object') || null
    : sigefRaw && typeof sigefRaw === 'object'
      ? sigefRaw
      : null;

  const first = parcels[0] && typeof parcels[0] === 'object' ? parcels[0] : null;

  let aggregatedSituacao = null;
  if (parcels.length > 0) {
    const situacoes = parcels
      .map((p) => {
        if (!p || typeof p !== 'object') return null;
        return p['situação'] ?? null;
      })
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '');

    if (situacoes.length > 0) {
      const normalized = situacoes.map((v) => String(v).trim().toLowerCase());
      const allCertificada = normalized.every((v) => v === 'certificada');
      aggregatedSituacao = allCertificada ? 'Certificada' : 'Registrada';
    }
  }

  const globalInfo = {
    codigoImovel:
      (rootFirst && (rootFirst.codigo_imo || rootFirst['código_imo'])) ||
      (first && (first.codigo_imo || first['código_imo'] || first.parcela_co)) ||
      null,
    municipio:
      (rootFirst && (rootFirst.municipio_ || rootFirst.municipio)) ||
      (first && (first.municipio_ || first.municipio)) ||
      null,
    uf: (rootFirst && rootFirst.uf) || (first && first.uf) || null,
    situacao:
      aggregatedSituacao ||
      (rootFirst &&
        (rootFirst.situacao || rootFirst.situacao_do_imovel || rootFirst['situação_do_imóvel'])) ||
      (first && (first.situacao || first.situacao_do_imovel || first['situação_do_imóvel'])) ||
      null,
  };

  return { parcels, globalInfo };
};

const mainFields = [
  {
    key: 'parcela_co',
    altKey: 'parcela_co',
    label: 'Código da Parcela',
  },
  {
    key: 'nome_da_propriedade',
    altKey: 'nome_da_propriedade',
    label: 'Nome da Propriedade',
  },
  {
    key: 'situação',
    altKey: 'situação',
    label: 'Situação do Imóvel',
  },
  {
    key: 'responsavel_tecnico',
    altKey: 'responsável_técnico',
    label: 'Responsável Técnico',
  },
  {
    key: 'data_da_aprovacao',
    altKey: 'data_da_aprovação',
    label: 'Data da Aprovação',
  },
  {
    key: 'matricula',
    altKey: 'matrícula',
    label: 'Matrícula',
  },
];

const getField = (obj, key, altKey) => {
  if (!obj) return null;
  if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  if (altKey && obj[altKey] !== undefined && obj[altKey] !== null) {
    return obj[altKey];
  }
  return null;
};

export default function ReportPrintSigefDetails({ snapshot }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  if (!isDomainEnabled('sigef')) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Domínio SIGEF desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  const properties =
    snapshot && snapshot.properties && typeof snapshot.properties === 'object'
      ? snapshot.properties
      : {};

  const sigefRaw = properties.sigef;
  const hasRawData = !!sigefRaw && (!Array.isArray(sigefRaw) || sigefRaw.length > 0);

  if (!hasRawData) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro SIGEF encontrado para este relatório.
        </Typography>
      </Box>
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { parcels, globalInfo } = useMemo(() => normalizeSigefData(sigefRaw), [sigefRaw]);
  const hasParcels = parcels && parcels.length > 0;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        SIGEF - Sistema de Gestão Fundiária
      </Typography>

      {isSectionEnabled('sigef', 'resumo_imovel') && (
        <ReportWidget sectionKey="sigef" widgetKey="sigef_resumo_imovel">
          <Card
            sx={{
              mb: 2,
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: 'warning.main',
              boxShadow: 1,
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                {isFieldEnabled('sigef', 'resumo_imovel', 'codigoImovel') && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {getFieldLabel('sigef', 'codigoImovel', 'Código do Imóvel')}
                    </Typography>
                    <ReportStyledField
                      domainKey="sigef"
                      fieldKey="codigoImovel"
                      variant="body1"
                      defaultSx={{ fontWeight: 600, wordBreak: 'break-all' }}
                    >
                      {globalInfo.codigoImovel || '-'}
                    </ReportStyledField>
                  </Grid>
                )}

                {isFieldEnabled('sigef', 'resumo_imovel', 'uf') && (
                  <Grid item xs={6} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {getFieldLabel('sigef', 'uf', 'UF')}
                    </Typography>
                    <ReportStyledField
                      domainKey="sigef"
                      fieldKey="uf"
                      variant="body1"
                      defaultSx={{ fontWeight: 500 }}
                    >
                      {globalInfo.uf || '-'}
                    </ReportStyledField>
                  </Grid>
                )}

                {isFieldEnabled('sigef', 'resumo_imovel', 'municipio') && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {getFieldLabel('sigef', 'municipio', 'Município')}
                    </Typography>
                    <ReportStyledField
                      domainKey="sigef"
                      fieldKey="municipio"
                      variant="body1"
                      defaultSx={{ fontWeight: 500 }}
                    >
                      {globalInfo.municipio || '-'}
                    </ReportStyledField>
                  </Grid>
                )}

                {isFieldEnabled('sigef', 'resumo_imovel', 'parcelas') && (
                  <Grid item xs={6} md={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {getFieldLabel('sigef', 'parcelas', 'Parcelas')}
                    </Typography>
                    <ReportStyledField
                      domainKey="sigef"
                      fieldKey="parcelas"
                      variant="body1"
                      defaultSx={{ fontWeight: 500 }}
                    >
                      {hasParcels
                        ? `${parcels.length} ${parcels.length === 1 ? 'parcela' : 'parcelas'}`
                        : '-'}
                    </ReportStyledField>
                  </Grid>
                )}

                {isFieldEnabled('sigef', 'resumo_imovel', 'situacao') && (
                  <Grid item xs={12} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, mt: 1 }}
                    >
                      {getFieldLabel('sigef', 'situacao', 'Situação do Imóvel')}
                    </Typography>
                    {globalInfo.situacao ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={globalInfo.situacao}
                          color={
                            String(globalInfo.situacao).trim().toLowerCase() === 'certificada'
                              ? 'success'
                              : 'warning'
                          }
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </ReportWidget>
      )}

      {!hasParcels && (
        <Typography variant="body2" color="text.secondary">
          Não foi possível identificar parcelas associadas a este registro SIGEF.
        </Typography>
      )}

      {hasParcels && (
        <Box>
          {parcels.map((parcel, index) => {
            if (!parcel || typeof parcel !== 'object') return null;

            const showParcelasPrincipais = isSectionEnabled('sigef', 'parcelas_principais');

            const parcelTitle =
              getField(parcel, 'nome_da_propriedade', 'nome_da_propriedade') ||
              getField(parcel, 'parcela_co', 'parcela_co') ||
              getField(parcel, 'codigo_imo', 'código_imo') ||
              `Parcela ${index + 1}`;

            const situacaoImovel = getField(parcel, 'situação', 'situação');

            return (
              <Box key={parcel.parcela_co || parcel.codigo_imo || index} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {parcelTitle}
                </Typography>

                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                  }}
                >
                  <CardContent>
                    {showParcelasPrincipais && (
                      <ReportWidget sectionKey="sigef" widgetKey="sigef_parcelas_principais">
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                          Informações principais
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {mainFields.map(({ key, altKey, label }) => {
                            if (!isFieldEnabled('sigef', 'parcelas_principais', key)) {
                              return null;
                            }
                            const rawValue = getField(parcel, key, altKey);
                            if (rawValue === null || rawValue === undefined || rawValue === '') {
                              return null;
                            }
                            const value =
                              key.indexOf('data') !== -1 ? formatDateBr(rawValue) : rawValue;
                            return (
                              <Grid item xs={12} md={4} key={key}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  {getFieldLabel('sigef', key, label)}
                                </Typography>
                                <ReportStyledField
                                  domainKey="sigef"
                                  fieldKey={key}
                                  variant="body2"
                                  defaultSx={{ fontWeight: 500 }}
                                >
                                  {value}
                                </ReportStyledField>
                              </Grid>
                            );
                          })}
                        </Grid>

                        {situacaoImovel && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600, display: 'block' }}
                            >
                              Situação do Imóvel (parcela)
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                size="small"
                                label={situacaoImovel}
                                color={
                                  String(situacaoImovel).trim().toLowerCase() === 'certificada'
                                    ? 'success'
                                    : 'warning'
                                }
                              />
                            </Box>
                          </Box>
                        )}

                        <Divider sx={{ my: 1.5 }} />
                      </ReportWidget>
                    )}

                    {isSectionEnabled('sigef', 'parcelas_outros_campos') && (
                      <ReportWidget sectionKey="sigef" widgetKey="sigef_parcelas_outros_campos">
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Outros campos
                        </Typography>
                        <Grid container spacing={2}>
                          {Object.entries(parcel)
                            .filter(
                              ([key]) =>
                                !mainFields.some(
                                  (field) => field.key === key || field.altKey === key,
                                ) &&
                                key !== 'geojson' &&
                                key !== 'geometry' &&
                                key !== 'feature',
                            )
                            .filter(([key]) =>
                              isFieldEnabled('sigef', 'parcelas_outros_campos', key),
                            )
                            .map(([key, value]) => {
                              if (value === null || value === undefined || value === '') {
                                return null;
                              }
                              return (
                                <Grid item xs={12} md={6} key={key}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                  >
                                    {getFieldLabel('sigef', key, humanizeKey(key))}
                                  </Typography>
                                  <ReportStyledField
                                    domainKey="sigef"
                                    fieldKey={key}
                                    variant="body2"
                                    defaultSx={{ fontWeight: 500 }}
                                  >
                                    {formatGenericValue(key, value)}
                                  </ReportStyledField>
                                </Grid>
                              );
                            })}
                        </Grid>
                      </ReportWidget>
                    )}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
